import { Component, lazy, Suspense, useEffect, useMemo, useRef, useState, type ErrorInfo, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { catalogSummary, type GameCategoryId } from '../../games/registry';
import { GameEntryLink } from '../../games/shared/GameEntryLink';
import { ClassicHomePage } from './ClassicHomePage';
import { planetGames, planetZones, trendingGames } from './home-data';
import './home.css';

const PlanetScene = lazy(() => import('./PlanetScene').then((module) => ({ default: module.PlanetScene })));

const FALLBACK_SESSION_KEY = 'inbox-3d-fallback-v1';

interface PlanetSceneBoundaryProps {
  children: ReactNode;
  onError: (message: string) => void;
}

class PlanetSceneBoundary extends Component<PlanetSceneBoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    this.props.onError(error.message || '3D 星球模块加载失败。');
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function readFallbackSession() {
  if (typeof window === 'undefined') return false;
  try { return window.sessionStorage.getItem(FALLBACK_SESSION_KEY) === '1'; } catch { return false; }
}

function rememberFallback() {
  try { window.sessionStorage.setItem(FALLBACK_SESSION_KEY, '1'); } catch { /* 存储不可用时由内存状态兜底 */ }
}

function clearFallback() {
  try { window.sessionStorage.removeItem(FALLBACK_SESSION_KEY); } catch { /* 存储不可用时由内存状态兜底 */ }
}

export function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState<GameCategoryId>('strategy');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [focusVersion, setFocusVersion] = useState(0);
  const [sceneState, setSceneState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [sceneError, setSceneError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [enteringSlug, setEnteringSlug] = useState<string | null>(null);
  const [sessionFallback, setSessionFallback] = useState(readFallbackSession);
  const [sceneRetryVersion, setSceneRetryVersion] = useState(0);
  const enterTimerRef = useRef<number | null>(null);
  const categoryButtonRefs = useRef<Partial<Record<GameCategoryId, HTMLButtonElement | null>>>({});

  const selectedGame = useMemo(() => planetGames.find((game) => game.slug === selectedSlug) ?? null, [selectedSlug]);
  const enteringGame = useMemo(() => planetGames.find((game) => game.slug === enteringSlug) ?? null, [enteringSlug]);
  const activeZone = planetZones.find((zone) => zone.id === activeCategory) ?? planetZones[1];
  const retryRequested = (location.state as { retry3d?: boolean } | null)?.retry3d === true;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  useEffect(() => () => {
    if (enterTimerRef.current !== null) window.clearTimeout(enterTimerRef.current);
  }, []);

  useEffect(() => {
    if (!retryRequested) return;
    clearFallback();
    setSessionFallback(false);
    setSceneError('');
    setSceneState('loading');
    setSceneRetryVersion((version) => version + 1);
    navigate('/', { replace: true, state: null });
  }, [navigate, retryRequested]);

  useEffect(() => {
    categoryButtonRefs.current[activeCategory]?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeCategory]);

  useEffect(() => {
    if (sessionFallback || sceneState !== 'loading') return undefined;
    const timer = window.setTimeout(() => handleSceneError('3D 星球首次可用画面超过 12 秒。'), 12_000);
    return () => window.clearTimeout(timer);
  }, [sceneState, sessionFallback]);

  useEffect(() => {
    if (retryRequested || ((!sessionFallback && sceneState !== 'error') || location.pathname !== '/')) return;
    navigate('/classic', { replace: true, state: { fallbackNotice: `${sceneError || '当前标签页已记录 3D 降级状态。'} 已为你打开经典 2D 首页。` } });
  }, [location.pathname, navigate, retryRequested, sceneError, sceneState, sessionFallback]);

  const selectGame = (slug: string | null) => {
    setSelectedSlug(slug);
    if (!slug) return;
    const game = planetGames.find((item) => item.slug === slug);
    if (game) setActiveCategory(game.category);
  };

  const chooseCategory = (category: GameCategoryId) => {
    setActiveCategory(category);
    setSelectedSlug(null);
  };

  const handleSceneError = (message: string) => {
    rememberFallback();
    setSessionFallback(true);
    setSceneError(message || '当前设备无法加载 3D 星球。');
    setSceneState('error');
  };

  const enterGame = (slug: string) => {
    if (enterTimerRef.current !== null) return;
    const game = planetGames.find((item) => item.slug === slug);
    if (!game) return;
    if (game.kind === 'external') {
      setSelectedSlug(slug);
      window.open(game.href, '_blank', 'noopener,noreferrer');
      return;
    }
    setMenuOpen(false);
    setEnteringSlug(slug);
    enterTimerRef.current = window.setTimeout(() => {
      enterTimerRef.current = null;
      navigate(game.path);
    }, 700);
  };

  if (sessionFallback || sceneState === 'error') {
    return <ClassicHomePage fallbackNotice={`${sceneError || '当前标签页已记录 3D 降级状态。'} 已为你打开经典 2D 首页。`} />;
  }

  return (
    <main className="planet-home">
      <div className="planet-brand">gameplanet 小游戏收藏 <small>{catalogSummary}</small></div>
      <Link className="planet-view-switch" to="/classic" aria-label="不习惯 3D？返回经典 2D 首页">
        <span>不习惯 3D？返回经典 2D 首页</span><b>经典 2D</b><i aria-hidden="true">↗</i>
      </Link>
      <div className="planet-hint">拖动星球 · 点击坐标</div>

      <Suspense fallback={null}>
        <PlanetSceneBoundary key={sceneRetryVersion} onError={handleSceneError}>
          <PlanetScene
            activeCategory={activeCategory}
            selectedSlug={selectedSlug}
            focusVersion={focusVersion}
            onSelect={selectGame}
            onActivate={enterGame}
            onReady={() => setSceneState('ready')}
            onError={handleSceneError}
          />
        </PlanetSceneBoundary>
      </Suspense>

      {sceneState === 'loading' && <div className="planet-loading" role="status"><span />正在点亮星球…</div>}

      <div className="planet-current-panel">
        <section className="planet-current-card" style={{ '--planet-accent': selectedGame?.accent ?? activeZone.color } as React.CSSProperties} aria-live="polite">
          <div className="planet-current-copy">
            <div className="planet-current-kicker">{selectedGame ? `${activeZone.label} · 游戏坐标` : `${activeZone.short} · 大陆`}</div>
            <h1>{selectedGame?.title ?? activeZone.label}</h1>
            <p>{selectedGame?.subtitle ?? activeZone.note}</p>
          </div>
          <div className="planet-current-actions">
            <button type="button" aria-label="重新定位当前目标" onClick={() => setFocusVersion((version) => version + 1)}><span aria-hidden="true">◎</span><span>定位</span></button>
            {selectedGame?.kind === 'external' && <GameEntryLink game={selectedGame} className="planet-start-game">打开外站</GameEntryLink>}
            {selectedGame?.kind === 'internal' && <button className="planet-start-game" type="button" aria-label={`开始游戏：${selectedGame.title}`} onClick={() => enterGame(selectedGame.slug)}><span aria-hidden="true">↗</span><span>开始游戏</span></button>}
          </div>
        </section>
      </div>

      <aside className="planet-trending" aria-label="Trending 主推榜">
        <header><strong>Trending</strong><span>编辑推荐</span></header>
        <div className="planet-trending__list">
          {trendingGames.map((game, index) => <GameEntryLink game={game} key={game.slug} onClick={game.kind === 'internal' ? (event) => { event.preventDefault(); enterGame(game.slug); } : undefined}><b>#{index + 1}</b><span><strong>{game.title}</strong><small>{game.subtitle}</small></span><i aria-hidden="true">↗</i></GameEntryLink>)}
        </div>
      </aside>

      <button className="planet-menu-trigger" type="button" aria-label="打开游戏菜单" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}><span aria-hidden="true">≡</span><strong>游戏菜单</strong></button>

      <aside className="planet-nav-dock" aria-label="大洲导航">
        <div className="planet-nav-title"><strong>大洲</strong><span>导航</span></div>
        <div className="planet-continent-list">
          {planetZones.map((zone) => (
            <button ref={(element) => { categoryButtonRefs.current[zone.id] = element; }} type="button" className={activeCategory === zone.id ? 'active' : ''} key={zone.id} aria-pressed={activeCategory === zone.id} onClick={() => chooseCategory(zone.id)}>
              <strong>{zone.short}</strong><small>{zone.count} 个入口</small>
            </button>
          ))}
        </div>
      </aside>

      {menuOpen && (
        <div className="planet-menu-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setMenuOpen(false); }}>
          <section className="planet-game-menu" role="dialog" aria-modal="true" aria-labelledby="planet-menu-title">
            <header><div><small>星球坐标</small><h2 id="planet-menu-title">游戏菜单</h2></div><button type="button" aria-label="关闭游戏列表" onClick={() => setMenuOpen(false)}>×</button></header>
            <div className="planet-game-menu__groups">
              {planetZones.map((zone) => (
                <section key={zone.id} aria-labelledby={`planet-menu-${zone.id}`}>
                  <h3 id={`planet-menu-${zone.id}`}>{zone.label}</h3>
                  <div>
                    {planetGames.filter((game) => game.category === zone.id).map((game) => (
                      <GameEntryLink game={game} key={game.slug} onClick={game.kind === 'internal' ? (event) => { event.preventDefault(); enterGame(game.slug); } : undefined} style={{ '--planet-accent': game.accent } as React.CSSProperties}>
                        <span>{game.symbol}</span><span><strong>{game.title}</strong><small>{game.subtitle}</small></span><b aria-hidden="true">↗</b>
                      </GameEntryLink>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </section>
        </div>
      )}

      {enteringGame && (
        <div className="planet-entering" role="status" aria-live="assertive" aria-label={`正在进入${enteringGame.title}`} style={{ '--planet-accent': enteringGame.accent } as React.CSSProperties}>
          <div className="planet-entering__symbol">{enteringGame.symbol}</div>
          <small>gameplanet · 小游戏收藏</small>
          <h2>{enteringGame.title}</h2>
          <p>正在进入游戏…</p>
          <span aria-hidden="true" />
        </div>
      )}
    </main>
  );
}
